import { useEffect, useMemo, useState } from 'react'
import { SharedFooter, SharedNav, useMeta } from '../shared'
import { professionCategories, getProfessionsForCategory } from './catalogue.js'
import { getProfessionCategoryContent } from './categoryContent.js'
import { getProfessionCategoryImage } from './images.js'
import './professionPages.css'

type Profession = {
  id: string
  name: string
  slug: string
  category: string
  categorySlug: string
  searchTerms: string[]
  canonicalHref: string
}

type Category = {
  name: string
  slug: string
  professionIds: string[]
}

const directoryDescription = 'Find Offlico guidance for mobile and on-site professionals. Browse by work type and see where bookings, client details and business admin fit together.'

function matchesProfession(profession: Profession, query: string) {
  if (!query) return true
  const searchable = [profession.name, profession.category, ...profession.searchTerms].join(' ').toLocaleLowerCase('en-GB')
  return searchable.includes(query)
}

export default function PageProfessionIndex() {
  useMeta('Mobile profession software | Offlico', directoryDescription)

  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const query = search.trim().toLocaleLowerCase('en-GB')

  useEffect(() => {
    const requestedCategory = new URLSearchParams(window.location.search).get('category')
    if (!requestedCategory || !professionCategories.some(category => category.slug === requestedCategory)) return
    setSelectedCategory(requestedCategory)
    window.requestAnimationFrame(() => document.getElementById('browse-professions')?.scrollIntoView())
  }, [])

  const chooseCategory = (categorySlug: string) => {
    setSelectedCategory(categorySlug)
    const url = new URL(window.location.href)
    if (categorySlug) url.searchParams.set('category', categorySlug)
    else url.searchParams.delete('category')
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
  }

  const visibleCategories = useMemo(() => {
    return (professionCategories as Category[])
      .filter(category => !selectedCategory || category.slug === selectedCategory)
      .map(category => ({
        category,
        professions: (getProfessionsForCategory(category.slug) as Profession[]).filter(profession => matchesProfession(profession, query)),
      }))
      .filter(group => group.professions.length > 0)
  }, [query, selectedCategory])

  const gallery = [
    'health-and-clinical-care',
    'vehicle-cycle-and-marine-services',
    'events-and-entertainment',
  ].map(getProfessionCategoryImage).filter((image): image is NonNullable<typeof image> => Boolean(image))

  return (
    <div className="op-page op-index-page">
      <SharedNav />
      <main>
        <section className="op-index-hero" aria-labelledby="op-index-title">
          <div className="op-container op-index-hero-inner">
            <div className="op-index-hero-copy">
              <p className="op-eyebrow">Mobile business types</p>
              <h1 id="op-index-title">Find Offlico for the work you do</h1>
              <p>Explore mobile and on-site professions, then see how Offlico’s scheduling, client and business tools may fit the shape of your day.</p>
              <button className="op-hero-link" type="button" onClick={() => document.getElementById('browse-professions')?.scrollIntoView()}>Browse professions <span aria-hidden="true">↗</span></button>
            </div>
            {gallery.length > 0 && (
              <div className="op-index-hero-gallery" aria-label="Examples of mobile and on-site work">
                {gallery.map((image, index) => (
                  <img
                    key={image.src}
                    className={`op-index-gallery-image op-index-gallery-image-${index + 1}`}
                    src={image.src}
                    alt={image.alt}
                    width={image.width}
                    height={image.height}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="op-browse" id="browse-professions" aria-labelledby="op-browse-title">
          <div className="op-container">
            <div className="op-browse-heading">
              <div>
                <p className="op-eyebrow">Browse by work type</p>
                <h2 id="op-browse-title">Choose a profession</h2>
                <p>Each page explains a common working pattern and where Offlico’s general business tools can help.</p>
              </div>
              <a href="/all-mobile-businesses/" className="op-text-link">See all mobile business features <span aria-hidden="true">↗</span></a>
            </div>

            <div className="op-filter-bar" role="search" aria-label="Find a profession">
              <label className="op-filter-field">
                <span>Search professions</span>
                <input
                  type="search"
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Search a job title or related term"
                  autoComplete="off"
                />
              </label>
              <label className="op-filter-field op-category-select">
                <span>Category</span>
                <select value={selectedCategory} onChange={event => chooseCategory(event.target.value)}>
                  <option value="">All categories</option>
                  {(professionCategories as Category[]).map(category => (
                    <option key={category.slug} value={category.slug}>{category.name}</option>
                  ))}
                </select>
              </label>
            </div>

            {visibleCategories.length > 0 ? (
              <div className="op-category-list">
                {visibleCategories.map(({ category, professions }) => {
                  const content = getProfessionCategoryContent(category.slug)
                  return (
                    <section className="op-category-section" key={category.slug} id={`category-${category.slug}`} aria-labelledby={`op-category-${category.slug}`}>
                      <div className="op-category-heading">
                        <h3 id={`op-category-${category.slug}`}>{category.name}</h3>
                        {content?.summary && <p>{content.summary}</p>}
                      </div>
                      <div className="op-profession-links">
                        {professions.map(profession => (
                          <a key={profession.id} href={profession.canonicalHref}>
                            <span>{profession.name}</span>
                            <span aria-hidden="true">↗</span>
                          </a>
                        ))}
                      </div>
                    </section>
                  )
                })}
              </div>
            ) : (
              <div className="op-empty-results" role="status">
                <h3>No matching profession found</h3>
                <p>Try another job title or browse every category.</p>
                <button type="button" onClick={() => { setSearch(''); chooseCategory('') }}>Show all professions</button>
              </div>
            )}
          </div>
        </section>
      </main>
      <SharedFooter />
    </div>
  )
}
